import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useCallback } from 'react';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { HotelProvidersLabels } from '../constants';
import { setHotelTripSearchInputs } from '../store/slices/tripSearch';
import { hotelSearch, updateHotelSearchObj } from '../store/slices/hotel';

const useContentSourceFilter = (tripId) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isHotelTripSearch = !tripId;

  const hotelSearchObj = useSelector((store) =>
    isHotelTripSearch
      ? store.tripSearch.searchInputs.hotel
      : store.hotel.hotelSearches[tripId]?.searchObj || {},
  );

  const { travelAgency = {}, isLoggedIn } = useSelector((store) => store.user);
  const doTakeHotelContentSourceInput = isLoggedIn
    ? travelAgency?.filterSettings?.doTakeHotelContentSourceInput
    : travelAgency?.filterSettings?.doTakeHotelContentSourceInputCFP;

  const { hotelProviders, selectedHotelProviders } = travelAgency;

  // Memoize options to prevent recalculating each render
  const contentSourceOptions = useMemo(() => {
    if (!hotelProviders) return [];
    return hotelProviders.map((provider) => ({
      value: provider,
      label: HotelProvidersLabels[provider] || provider,
    }));
  }, [hotelProviders]);

  // Dispatch default values if not already set
  useMemo(() => {
    let providerValues = contentSourceOptions.map((option) => option.value);
    if (selectedHotelProviders && selectedHotelProviders.length > 0) {
      providerValues = _.filter(providerValues, (value) =>
        _.find(selectedHotelProviders, (v) => v === value),
      );
    }
    const currentValues = hotelSearchObj?.hotelProvidersToInclude ?? [];

    if (providerValues.length && currentValues.length === 0 && isHotelTripSearch) {
      dispatch(setHotelTripSearchInputs({ hotelProvidersToInclude: providerValues }));
    } else if (providerValues.length && currentValues.length === 0 && !isHotelTripSearch) {
      const payload = {
        tripId,
        searchObj: {
          page: 1,
          hotelProvidersToInclude: providerValues,
        },
      };
      dispatch(updateHotelSearchObj(payload));
    }
  }, [
    dispatch,
    contentSourceOptions,
    hotelSearchObj,
    selectedHotelProviders,
    isHotelTripSearch,
    tripId,
  ]);

  // Handle provider selection changes
  const handleSourceChange = useCallback(
    (value) => {
      const existingValues = hotelSearchObj.hotelProvidersToInclude || [];
      const newValues = existingValues.includes(value)
        ? existingValues.filter((v) => v !== value)
        : [...existingValues, value];

      if (newValues.length === 0) return; // At least one must be selected

      const payload = {
        tripId,
        searchObj: {
          page: 1,
          hotelProvidersToInclude: newValues,
        },
      };

      if (!newValues.includes(HotelProvidersLabels.SABRE_LODGING)) {
        payload.searchObj.rateCodes = undefined;
      }

      if (isHotelTripSearch) {
        dispatch(setHotelTripSearchInputs({ ...payload.searchObj }));
      } else {
        dispatch(hotelSearch(payload));
      }

      track('rateCodePreferencesFilter.handleSourceChange', {
        filterAmenities: JSON.stringify(value),
      });
    },
    [dispatch, hotelSearchObj, isHotelTripSearch, track, tripId],
  );

  return {
    doTakeHotelContentSourceInput,
    contentSourceOptions,
    selectedValues: hotelSearchObj?.hotelProvidersToInclude || [],
    handleSourceChange,
  };
};

export default useContentSourceFilter;
